import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Share, PlusSquare, X } from "lucide-react";
import { getInstallPrompt, clearInstallPrompt, onInstallChange, isStandalone, isIOS, isAndroid, isPwaInstalled, accountHasDesktopPwa, shouldReportPwaLaunch, desktopOs } from "@/lib/installPrompt";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { viaSharePath } from "@/lib/shareLinks";
import { isBot } from "@/lib/bots";

const DISMISS_KEY = "yalola.installDismissed";
const POPUP_DISMISS_KEY = "yalola.installPopupDismissed";
const STORE_DISMISS_KEY = "yalola.storePopupDismissed";
const LAUNCH_REPORTED_KEY = "yalola.pwaLaunchReported";
const DEFAULT_PLAY_URL = "https://play.google.com/store/apps/details?id=app.yalola";
// Set by the Stripe return page when the website is shown inside the native app's
// in-app browser: the app IS installed, never propose it there.
const inAppBrowser = () => { try { return sessionStorage.getItem("yalola.inAppBrowser") === "1"; } catch { return false; } };

export const InstallAppButton = ({ variant = "sidebar" }) => {
  const { t, config, lang, user } = useApp();
  const [tick, force] = useState(0);
  const [iosOpen, setIosOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISS_KEY) === "1");
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(() => localStorage.getItem(POPUP_DISMISS_KEY) === "1");
  const [storeOpen, setStoreOpen] = useState(false);
  const [storeDismissed, setStoreDismissed] = useState(() => localStorage.getItem(STORE_DISMISS_KEY) === "1");
  useEffect(() => onInstallChange(() => force((x) => x + 1)), []);
  // Installed desktop PWA opened by a signed-in member → remember it on the account
  // (once per session) so every browser/profile of this machine hides the install.
  useEffect(() => {
    if (variant !== "popup" || !user?.id || !shouldReportPwaLaunch()) return;
    try { if (sessionStorage.getItem(LAUNCH_REPORTED_KEY) === "1") return; sessionStorage.setItem(LAUNCH_REPORTED_KEY, "1"); } catch { /* noop */ }
    api.post("/users/me/pwa-launch", { os: desktopOs() }).catch(() => {});
  }, [variant, user?.id]);
  const knownInstalled = () => isStandalone() || inAppBrowser() || isPwaInstalled() || accountHasDesktopPwa(user);

  const pwaCfg = config?.settings?.pwa || {};
  const androidUrl = pwaCfg.store_android_url || DEFAULT_PLAY_URL;
  const iosUrl = pwaCfg.store_ios_url || "";
  // Phones browsing yalola.app get THEIR store only: Google Play on Android, the App
  // Store on iPhone/iPad — and nothing at all on iOS until the App Store URL exists
  // (Admin › Paramètres › PWA). Never a Google Play badge on an iPhone.
  const storeMobile = isAndroid() || (isIOS() && !!iosUrl);

  // Store banner: auto-open ONCE on Android/iOS browsers (first visit). Skipped when
  // the native app is detected as installed (best-effort, Chrome Android only).
  useEffect(() => {
    if (variant !== "popup" || !storeMobile || storeDismissed) return undefined;
    if (knownInstalled() || viaSharePath()) return undefined; // « Ouvrir dans l'app » banner takes over
    let cancelled = false;
    const tmo = setTimeout(async () => {
      try {
        if (window.navigator.getInstalledRelatedApps) {
          const apps = await window.navigator.getInstalledRelatedApps();
          if (apps && apps.length) return; // native app already installed
        }
      } catch { /* noop */ }
      if (!cancelled) setStoreOpen(true);
    }, 1200);
    return () => { cancelled = true; clearTimeout(tmo); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, storeMobile, storeDismissed]);

  // PWA popup: auto-open once on unsupported-store devices (Windows/Mac/Linux desktop,
  // other phones) when the browser can actually install (beforeinstallprompt captured).
  useEffect(() => {
    if (variant !== "popup" || storeMobile || popupDismissed) return undefined;
    if (knownInstalled()) return undefined;
    if (!getInstallPrompt()) return undefined;
    const tmo = setTimeout(() => setPopupOpen(true), 1200);
    return () => clearTimeout(tmo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, storeMobile, popupDismissed, tick, user?.pwa_desktop]);

  // Admin can disable install prompts entirely.
  if (config?.settings?.pwa?.install_enabled === false) return null;
  if (knownInstalled()) return null;
  // Invités et robots : jamais de pop-up ni de bandeau « Installer l'app » (le contenu d'abord — SEO / AdSense).
  if ((variant === "popup" || variant === "banner") && (!user || isBot())) return null;
  const prompt = getInstallPrompt();
  const ios = isIOS();
  if (!storeMobile && !prompt && !ios) return null;
  if (variant === "banner" && dismissed) return null;

  const install = async () => {
    if (prompt) {
      prompt.prompt();
      try {
        const { outcome } = await prompt.userChoice;
        if (outcome === "accepted") clearInstallPrompt();
      } catch { /* noop */ }
    } else {
      setIosOpen(true);
    }
  };
  const dismiss = () => { localStorage.setItem(DISMISS_KEY, "1"); setDismissed(true); };
  const popupLater = () => { localStorage.setItem(POPUP_DISMISS_KEY, "1"); setPopupDismissed(true); setPopupOpen(false); };
  const popupInstall = async () => { setPopupOpen(false); await install(); };
  const storeLater = () => { localStorage.setItem(STORE_DISMISS_KEY, "1"); setStoreDismissed(true); setStoreOpen(false); };

  // Official store badges, localized in the app's 4 languages (self-hosted).
  const badgeLang = ["fr", "en", "es", "pt"].includes(lang) ? lang : "fr";
  const playBadge = `/badges/google-play-badge-${badgeLang}.png`;
  const appStoreBadge = `/badges/app-store-badge-${badgeLang}.svg`;

  const storeBadges = (size = "h-12") => (
    <div className="flex items-center justify-center gap-2.5 flex-wrap">
      {isIOS() ? (
        <a href={iosUrl} target="_blank" rel="noopener noreferrer" data-testid="store-badge-appstore" aria-label="App Store">
          <img src={appStoreBadge} alt="App Store" className={`${size} w-auto`} style={{ padding: "6.5%" }} />
        </a>
      ) : (
        <a href={androidUrl} target="_blank" rel="noopener noreferrer" data-testid="store-badge-play" aria-label="Google Play">
          <img src={playBadge} alt="Google Play" className={`${size} w-auto`} />
        </a>
      )}
    </div>
  );

  const storesDialog = (
    <Dialog open={storeOpen} onOpenChange={(o) => { if (!o) storeLater(); else setStoreOpen(true); }}>
      <DialogContent aria-describedby={undefined} className="max-w-sm text-center" data-testid="store-install-popup">
        <DialogHeader>
          <DialogTitle className="font-display text-center">{t("install.store_title", "Télécharge l'application Yalola")}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col items-center gap-3 pt-1">
          <img src="/logo192.png" alt="Yalola" className="h-16 w-16 rounded-2xl shadow" />
          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
            {t("install.store_body", "Profite de l'expérience complète : directs, notifications et vidéos plus fluides avec l'application officielle.")}
          </p>
          {storeBadges("h-12")}
          <button type="button" onClick={storeLater} className="text-sm font-medium py-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="store-popup-later">
            {t("install.popup_later", "Plus tard")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );

  const iosDialog = (
    <Dialog open={iosOpen} onOpenChange={setIosOpen}>
      <DialogContent aria-describedby={undefined} className="max-w-sm" data-testid="ios-install-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("install.ios_title", "Installer Yalola sur iPhone")}</DialogTitle></DialogHeader>
        <ol className="text-sm space-y-3 mt-1">
          <li className="flex items-center gap-2"><Share size={18} style={{ color: "var(--yalola-primary)" }} /> {t("install.ios_step1", "1. Touchez le bouton Partager de Safari")}</li>
          <li className="flex items-center gap-2"><PlusSquare size={18} style={{ color: "var(--yalola-primary)" }} /> {t("install.ios_step2", "2. Choisissez « Sur l'écran d'accueil »")}</li>
          <li>{t("install.ios_step3", "3. Touchez Ajouter — Yalola s'ouvrira comme une application 🎉")}</li>
        </ol>
      </DialogContent>
    </Dialog>
  );

  if (variant === "popup") {
    if (storeMobile) return storesDialog;
    return (
      <>
        <Dialog open={popupOpen} onOpenChange={(o) => { if (!o) popupLater(); else setPopupOpen(true); }}>
          <DialogContent aria-describedby={undefined} className="max-w-sm text-center" data-testid="install-popup">
            <DialogHeader>
              <DialogTitle className="font-display text-center">{t("install.popup_title", "Installe l'application Yalola")}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center gap-3 pt-1">
              <span className="h-16 w-16 rounded-2xl grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
                <Download size={30} style={{ color: "var(--yalola-primary)" }} />
              </span>
              <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>
                {t("install.popup_body", "Accède plus vite à Yalola, reçois les notifications et profite d'une expérience plein écran comme une vraie appli.")}
              </p>
              <div className="flex flex-col w-full gap-2 pt-1">
                <Button className="pill-primary rounded-full w-full h-11" onClick={popupInstall} data-testid="install-popup-cta">
                  <Download size={16} className="mr-1.5" /> {t("install.cta", "Installer")}
                </Button>
                <button type="button" onClick={popupLater} className="text-sm font-medium py-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="install-popup-later">
                  {t("install.popup_later", "Plus tard")}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {iosDialog}
      </>
    );
  }

  if (variant === "banner") {
    if (storeMobile) {
      return (
        <div className="lg:hidden max-w-[1400px] mx-auto px-3 sm:px-6 pt-3" data-testid="install-banner">
          <div className="rounded-2xl px-4 py-2.5 flex items-center gap-3" style={{ background: "var(--yalola-primary-soft)" }}>
            <div className="flex-1 text-sm font-medium">{t("install.store_banner", "L'app Yalola est disponible")}</div>
            {storeBadges("h-9")}
            <button type="button" onClick={dismiss} aria-label="Fermer" data-testid="install-banner-dismiss" style={{ color: "var(--yalola-text-muted)" }}><X size={16} /></button>
          </div>
        </div>
      );
    }
    return (
      <div className="lg:hidden max-w-[1400px] mx-auto px-3 sm:px-6 pt-3" data-testid="install-banner">
        <div className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: "var(--yalola-primary-soft)" }}>
          <Download size={18} style={{ color: "var(--yalola-primary)" }} />
          <div className="flex-1 text-sm font-medium">{t("install.banner", "Installez Yalola sur votre téléphone")}</div>
          <Button size="sm" className="pill-primary rounded-full" onClick={install} data-testid="install-app-btn-banner">{t("install.cta", "Installer")}</Button>
          <button type="button" onClick={dismiss} aria-label="Fermer" data-testid="install-banner-dismiss" style={{ color: "var(--yalola-text-muted)" }}><X size={16} /></button>
        </div>
        {iosDialog}
      </div>
    );
  }

  return (
    <>
      <button type="button" onClick={() => (storeMobile ? setStoreOpen(true) : install())} className="w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors hover:bg-[rgba(0,0,0,0.05)] mt-1" data-testid="install-app-btn">
        <span className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "#0EA5E91f" }}>
          <Download size={18} color="#0EA5E9" strokeWidth={2.1} />
        </span>
        <span className="text-[14px] font-medium" style={{ color: "var(--yalola-text)" }}>{t("install.button", "Installer l'application")}</span>
      </button>
      {storeMobile ? storesDialog : iosDialog}
    </>
  );
};
